<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            ajax-path="admin/providers"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-3/4 lg:w-1/3"
            @close="editModalClosed"
        >
            <template slot="header">Edit Provider</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />
                <div class="mt-5 mb-3 text-xs text-gray-600"><span class="text-red-500">*</span> JSON Config</div>
                <prism-editor
                    v-model="editForm.config"
                    :code="editForm.config"
                    lang="json"
                    @change="editForm.errors.clear('config')" 
                />
                <div 
                    class="text-red-500 text-sm mt-2"
                    v-if="editForm.errors.has('config')"
                >{{ editForm.errors.get('config') }}</div>                 
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-3/4 lg:w-1/3"
            @close="addModalClosed"
        >
            <template slot="header">Add Provider</template>
            <template slot="body">
                <app-input
                    v-model="addForm.name"
                    placeholder="Name"
                    :error="addForm.errors.get('name')"
                    @updated="addForm.errors.clear('name')"
                    focus
                    required
                />
                <div class="mt-5 mb-3 text-xs text-gray-600"><span class="text-red-500">*</span> JSON Config</div>
                <prism-editor
                    v-model="addForm.config"
                    :code="addForm.config"
                    lang="json"
                    @change="addForm.errors.clear('config')" 
                />
                <div 
                    class="text-red-500 text-sm mt-2"
                    v-if="addForm.errors.has('config')"
                >{{ addForm.errors.get('config') }}</div>                
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import "prismjs";
    import PrismEditor from 'vue-prism-editor';
    
    export default {
        components: {
            PrismEditor,
        },
        data() {
            return {
                addForm: new Form({
                    name: null,
                    config: JSON.stringify({}, null, 2),
                }),
                editForm: new Form({
                    id: null,
                    name: null,
                    config: JSON.stringify({}, null, 2),
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                refreshTable: false,
                columns: [
                    {
                        title: 'Name', 
                        field: 'name', 
                        sorter: 'string',
                        minWidth: 160,
                    },
                    {
                        title: 'Config', 
                        field: 'config', 
                        sorter: 'string',
                        minWidth: 250,
                        variableHeight: true,
                        formatter(cell) {
                            return JSON.stringify(cell._cell.value, null, 2);
                        }  
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: providers => {
                            const provider = providers[0]
                            this.showEditModal = true;
                            this.editForm.set({
                                id: provider.id,
                                name: provider.name,
                                config: JSON.stringify(provider.config, null, 2),
                            });
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: providers => {
                            this.deleteProvider(providers[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        methods: {
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update(params) {
                this.editFormIsLoading = true;
                this.$store.dispatch('admin/providers/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Provider was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            add() {
                this.showAddModal = true;
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('admin/providers/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Provider was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },            
            deleteProvider(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${user.name}</strong>" provider permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/providers/delete', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Provider was successfully deleted.'
                        })
                    }
                })                
            }
        }
    }
</script>